import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";

const EmployerPipeline = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState("Recommended Matches");
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Dynamic tabs based on actual candidate data
    const tabs = [
        { name: "Recommended Matches", count: candidates.filter(c => parseInt(c.matchPercentage) >= 80).length },
        { name: "Candidate Directory", count: candidates.length }
    ];

    // Fetch candidates data
    useEffect(() => {
        fetchCandidates();
    }, []);

    const fetchCandidates = async () => {
        try {
            setLoading(true);
            
            // Get all users with Employee role from users_profiles table
            const { data: employeeUsers, error: usersError } = await supabase
                .from('users_profiles')
                .select(`
                    profileid, role, email, firstname, lastname,
                    profession, location_city_country, profile_about,
                    profile_image_url, linkedinurl, jobsearchstatus,
                    skills, languages, work_experiences, educations,
                    selected_roles, employment_types, profile_visibility
                `)
                .eq('role', 'Employee');

            if (usersError) {
                throw usersError;
            }

            if (!employeeUsers || employeeUsers.length === 0) {
                setCandidates([]);
                return;
            }

            // Get all applications from this employer to check for "current employer" status
            let employerApplicationUserIds = new Set();
            if (user?.id) {
                try {
                    // Get all job IDs for this employer
                    const { data: employerJobs } = await supabase
                        .from('internal_job_listings')
                        .select('id')
                        .eq('employer_id', user.id);
                    
                    if (employerJobs && employerJobs.length > 0) {
                        const jobIds = employerJobs.map(j => j.id);
                        const { data: applications } = await supabase
                            .from('applications')
                            .select('user_id')
                            .in('job_listing_id', jobIds);
                        
                        if (applications) {
                            employerApplicationUserIds = new Set(applications.map(app => app.user_id));
                        }
                    }
                } catch (err) {
                    // Error fetching employer applications
                }
            }

            // Transform users_profiles data into candidate objects
            const candidatesData = employeeUsers.map((user) => {
                try {
                    const firstName = user.firstname || '';
                    const lastName = user.lastname || '';
                    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Employee User';
                    
                    // Parse JSON fields
                    const skills = user.skills ? JSON.parse(user.skills) : [];
                    const languages = user.languages ? JSON.parse(user.languages) : [];
                    const workExperiences = user.work_experiences ? JSON.parse(user.work_experiences) : [];
                    const educations = user.educations ? JSON.parse(user.educations) : [];
                    const selectedRoles = user.selected_roles ? JSON.parse(user.selected_roles) : [];
                    
                    // Create tags from skills (limit to 3)
                    const tags = skills.slice(0, 3).map(skill => 
                        typeof skill === 'string' ? skill : skill.name
                    );
                    
                    // Get profile image URL
                    let profileImageUrl = null;
                    if (user.profile_image_url) {
                        if (user.profile_image_url.startsWith('http')) {
                            profileImageUrl = user.profile_image_url;
                        } else {
                            const { data: imageData } = supabase.storage
                                .from('profile-images')
                                .getPublicUrl(user.profile_image_url);
                            profileImageUrl = imageData.publicUrl;
                        }
                    }
                    
                    // Calculate match percentage based on profile completeness
                    const completeness = calculateProfileCompleteness({
                        first_name: firstName,
                        last_name: lastName,
                        profession: user.profession,
                        location_city_country: user.location_city_country,
                        profile_about: user.profile_about,
                        skills: skills,
                        work_experiences: workExperiences,
                        educations: educations,
                        languages: languages,
                        linkedin_url: user.linkedinurl,
                        job_search_status: user.jobsearchstatus,
                        hpcsa_reg_no: user.hpcsa_reg_no
                    });
                    const matchPercentage = Math.min(100, Math.max(0, completeness));
                    
                    return {
                        id: user.profileid,
                        name: fullName,
                        role: user.profession || 'Medical Professional',
                        location: user.location_city_country || 'Location not specified',
                        matchPercentage: `${matchPercentage}%`,
                        matchLabel: "Profile Match",
                        tags: tags,
                        profileImageUrl: profileImageUrl,
                        email: user.email || 'email@example.com',
                        aboutText: user.profile_about || 'Profile information not available',
                        skills: skills,
                        workExperiences: workExperiences,
                        educations: educations,
                        languages: languages,
                        linkedinUrl: user.linkedinurl || '',
                        jobSearchStatus: user.jobsearchstatus || '',
                        profileData: {
                            selectedRoles: selectedRoles,
                            employmentTypes: user.employment_types ? JSON.parse(user.employment_types) : []
                        }
                    };
                } catch (err) {
                    return null;
                }
            });

            // Filter out null results
            const validCandidates = candidatesData.filter(candidate => candidate !== null);
            
            // Apply profile visibility filtering
            const visibilityFilteredCandidates = validCandidates.filter(candidate => {
                const user = employeeUsers.find(u => u.profileid === candidate.id);
                if (!user) return false;
                
                const visibility = user.profile_visibility;
                
                // If visibility is 'public', show to all
                if (visibility === 'public') {
                    return true;
                }
                
                // If visibility is 'hidden', hide from all employers
                if (visibility === 'hidden') {
                    return false;
                }
                
                // If visibility is 'invisible-current', hide from employers who have applications
                if (visibility === 'invisible-current') {
                    return !employerApplicationUserIds.has(candidate.id);
                }
                
                // Default: if visibility is not set or unknown, show (backward compatibility)
                return true;
            });
            
            setCandidates(visibilityFilteredCandidates);
        } catch (error) {
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateProfileCompleteness = (metadata) => {
        let score = 0;
        const checks = [
            // Basic info
            metadata.first_name && metadata.last_name,
            metadata.location_city_country || metadata.country,
            metadata.profile_about,
            
            // Skills and experience
            metadata.skills?.length > 0,
            metadata.work_experiences?.length > 0,
            metadata.educations?.length > 0,
            
            // Additional info
            metadata.languages?.length > 0,
            metadata.linkedin_url,
            metadata.job_search_status,
            metadata.hpcsa_reg_no
        ];
        
        const completedChecks = checks.filter(check => check).length;
        score = Math.round((completedChecks / checks.length) * 100);
        
        return Math.max(0, Math.min(100, score)); // Return actual score between 0-100%
    };

    const handleViewProfile = (candidateId) => {
        navigate(`/employer/candidate/${candidateId}`);
    };

    const filteredCandidates = candidates.filter(candidate => {
        // Apply search filter
        const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            candidate.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
            candidate.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
            candidate.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
        
        // Apply tab filter
        if (selectedTab === "Recommended Matches") {
            return matchesSearch && parseInt(candidate.matchPercentage) >= 80;
        }
        
        return matchesSearch;
    });

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employer-pipeline-page {
                    padding-bottom: 3rem !important;
                }
                .employer-pipeline-header {
                    padding: 1rem !important;
                }
                .employer-pipeline-header h1 {
                    font-size: 1.75rem !important;
                }
                .employer-pipeline-content {
                    padding: 1rem !important;
                }
                .employer-pipeline-tabs {
                    gap: 1rem !important;
                    padding-bottom: 0.5rem !important;
                    -webkit-overflow-scrolling: touch;
                }
                .employer-pipeline-tabs button {
                    font-size: 0.95rem !important;
                    padding-bottom: 0.25rem !important;
                }
                .employer-pipeline-search input {
                    width: 100% !important;
                    font-size: 0.9rem !important;
                    padding: 0.65rem 2.5rem 0.65rem 2.5rem !important;
                }
                .employer-pipeline-search .absolute {
                    padding-left: 0.5rem !important;
                }
                .employer-pipeline-grid {
                    gap: 0.75rem !important;
                }
                .employer-pipeline-card {
                    padding: 1rem !important;
                }
                .employer-pipeline-card .text-lg {
                    font-size: 1rem !important;
                }
                .employer-pipeline-card .text-sm {
                    font-size: 0.85rem !important;
                }
                .employer-pipeline-card-content {
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                }
                .employer-pipeline-card-actions {
                    width: 100% !important;
                    margin-left: 0 !important;
                }
                .employer-pipeline-card-actions button {
                    width: 100% !important;
                    font-size: 0.85rem !important;
                    padding: 0.6rem 0.75rem !important;
                }
                .employer-pipeline-avatar {
                    width: 3rem !important;
                    height: 3rem !important;
                }
                .employer-pipeline-tags {
                    gap: 0.35rem !important;
                }
                .employer-pipeline-load-more {
                    width: 100% !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-white employer-pipeline-page">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 employer-pipeline-header">
                <div className="flex items-center">
                    {/* Title */}
                    <h1 className="text-3xl font-bold text-gray-900">Job Pipeline</h1>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-6 py-6 employer-pipeline-content">
                {/* Navigation Tabs */}
                <div className="mb-6">
                    <div className="flex space-x-8 overflow-x-auto employer-pipeline-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.name}
                                onClick={() => setSelectedTab(tab.name)}
                                className={`flex items-center space-x-2 text-lg font-medium pb-2 border-b-2 transition-colors ${
                                    selectedTab === tab.name
                                        ? 'text-gray-900 border-gray-900'
                                        : 'text-gray-400 border-transparent hover:text-gray-600'
                                }`}
                            >
                                <span>{tab.name}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    selectedTab === tab.name
                                        ? 'bg-black text-white'
                                        : 'bg-gray-100 text-gray-700'
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                    <div className="relative max-w-2xl employer-pipeline-search">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none employer-pipeline-search-icon">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search Talent..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Results Count */}
                <div className="mb-6">
                    <p className="text-sm text-gray-500">
                        {loading ? 'Loading candidates...' : `Showing ${filteredCandidates.length} of ${candidates.length} candidates`}
                    </p>
                </div>

                {/* Candidate Cards */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 employer-pipeline-grid">
                        {filteredCandidates.map((candidate) => (
                            <div key={candidate.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow employer-pipeline-card">
                                <div className="flex items-start space-x-4">
                                    {/* Avatar */}
                                    <div className="flex-shrink-0">
                                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden employer-pipeline-avatar">
                                            {candidate.profileImageUrl ? (
                                                <img 
                                                    src={candidate.profileImageUrl} 
                                                    alt={candidate.name} 
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <span className="text-lg font-medium text-gray-500">
                                                    {candidate.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between employer-pipeline-card-content">
                                            <div className="flex-1">
                                                {/* Name - Clickable */}
                                                <h3 
                                                    onClick={() => handleViewProfile(candidate.id)}
                                                    className="text-lg font-semibold text-gray-900 mb-1 cursor-pointer hover:text-gray-700 hover:underline transition-colors"
                                                >
                                                    {candidate.name}
                                                </h3>
                                                
                                                {/* Role */}
                                                <p className="text-sm text-gray-900 mb-1">
                                                    {candidate.role}
                                                </p>
                                                
                                                {/* Location */}
                                                <p className="text-sm text-gray-600 mb-3">
                                                    {candidate.location}
                                                </p>

                                                {/* Tags */}
                                                <div className="flex flex-wrap items-center gap-2 mb-4 employer-pipeline-tags">
                                                    {candidate.tags.map((tag, index) => (
                                                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex-shrink-0 ml-4 flex flex-col space-y-2 employer-pipeline-card-actions">
                                                <button 
                                                    onClick={() => handleViewProfile(candidate.id)}
                                                    className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                                                >
                                                    View Profile
                                                </button>
                                                <button className="bg-white text-black border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                                                    Send message
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Load More Button */}
                <div className="mt-8 text-center">
                    <button className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors employer-pipeline-load-more">
                        Load More Candidates
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default EmployerPipeline;


